import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { HelpIcon } from './HelpIcon';
import { openBugReport } from '../lib/clientMessaging';
import { Folder, SettingsFuture, PuzzlePiece, Users, ServerStack, LinkChain, BarChart } from '../assets/icons/coolicons';
import { useAuth, useConnectorsVisible } from '../contexts/AuthContext';
import { conversationsAPI } from '../services/api';
import { CHAT_MODE_HIDDEN, WORKSPACE_MODE_KEY } from '../lib/workspaceMode';
import NotificationBell from './notifications/NotificationBell';
import { useCoachMark } from './coach/CoachMarkProvider';
import { BulbIcon } from './coach/BulbIcon';
import { tourForRoute, COACH_TOURS } from '../lib/coachTours';
import { getTourRecord } from '../lib/coachJourney';
import { currentStep, tourGlowState } from '../lib/coachRun';
import { readCoachState } from '../lib/coachDom';
import { useBilling } from '../contexts/BillingContext';
import TaskCreditMeter from './billing/TaskCreditMeter';

// Sidebar-toggle icon — Lucide `panel-left` shape per design system §16.
// Rectangle + vertical line conveys "this is a sidebar"; flipping the chevron
// inside it is unnecessary noise — the position of the sidebar already tells
// the user which way it'll move.
export const PanelToggleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="9" y1="3" x2="9" y2="21" />
  </svg>
);

export const SIDEBAR_W_EXPANDED = 224;
export const SIDEBAR_W_COLLAPSED = 64;
// Drag-resize clamps for the expanded sidebar (desktop only). Wide enough to
// stop truncating chat titles, narrow enough that it can't eat the chat panel.
// Ceiling came down from 400 with the default: at 400 the rail stopped reading
// as chrome and started reading as a second column.
export const SIDEBAR_MIN_W = 200;
export const SIDEBAR_MAX_W = 340;

// Styled hover tooltip for the collapsed icon rail — replaces the native
// `title` attr (slow to appear, unstyled). Parent needs `relative group`.
// Shows on hover (after a beat) and on keyboard focus.
const RailTooltip: React.FC<{ label: string }> = ({ label }) => (
  <span
    aria-hidden="true"
    className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md border border-white/10 bg-[#1c1c1f] px-2 py-1 text-xs font-medium text-gray-200 opacity-0 shadow-lg transition-opacity duration-100 group-hover:opacity-100 group-hover:delay-150 group-focus-visible:opacity-100"
  >
    {label}
  </span>
);

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  /** Phone: opens the drawer (the tour opens it for a card that points inside it). */
  onMobileOpen?: () => void;
  // Drag-resize (desktop, expanded only). Width state lives in App.tsx because
  // it also drives <main>'s padding-left; the sidebar just renders the handle.
  expandedWidth?: number;
  onResize?: (width: number) => void;
  onResizingChange?: (resizing: boolean) => void;
}

interface NavItemSpec {
  to: string;
  label: string;
  Icon: React.ComponentType<{ size?: number; className?: string }>;
  // When true, this nav item opens an in-app modal instead of navigating
  // to a route. The Link target becomes `<current-pathname>?<modalParam>`
  // so the URL still drives the modal (back button, deep links, refresh
  // all keep working), and keyboard a11y is preserved — unlike an
  // onClick-only button.
  modal?: boolean;
  modalParam?: string;
  // When true, isActive() requires an exact pathname match instead of also
  // matching subpaths (`path + '/'`). Needed once a sibling nav item's `to`
  // is a subpath of this one's — e.g. Dashboard (/ops-moonlight-admin) vs
  // Machines (/ops-moonlight-admin/machines) — or both would light up on
  // /ops-moonlight-admin/machines.
  exact?: boolean;
}

// The two top-level surfaces, plus the staff-only Admin surface. Build = the
// app-builder composer + your apps (route /apps, kept as-is — the tab is just
// labelled "Build"). Chat = the general-AI surface (/chat home, /chat/:id
// conversations). Admin is never a segmented-control choice and never
// persisted — see WorkspaceMode below.
type Mode = 'build' | 'chat' | 'admin';
// The subset of Mode that gets remembered across page loads (storedMode /
// WORKSPACE_MODE_KEY). Admin is reached only via its own route or the
// sidebar's Admin nav item, so it's deliberately excluded — landing on
// /ops-moonlight-admin must never "stick" as your home surface.
type WorkspaceMode = 'build' | 'chat';

const ChatModeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <path d="M5.59961 19.9203L7.12357 18.7012L7.13478 18.6926C7.45249 18.4384 7.61281 18.3101 7.79168 18.2188C7.95216 18.1368 8.12328 18.0771 8.2998 18.0408C8.49877 18 8.70603 18 9.12207 18H17.8031C18.921 18 19.4806 18 19.908 17.7822C20.2843 17.5905 20.5905 17.2842 20.7822 16.9079C21 16.4805 21 15.9215 21 14.8036V7.19691C21 6.07899 21 5.5192 20.7822 5.0918C20.5905 4.71547 20.2837 4.40973 19.9074 4.21799C19.4796 4 18.9203 4 17.8002 4H6.2002C5.08009 4 4.51962 4 4.0918 4.21799C3.71547 4.40973 3.40973 4.71547 3.21799 5.0918C3 5.51962 3 6.08009 3 7.2002V18.6712C3 19.7369 3 20.2696 3.21846 20.5433C3.40845 20.7813 3.69644 20.9198 4.00098 20.9195C4.35115 20.9191 4.76744 20.5861 5.59961 19.9203Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BuildModeIcon: React.FC<{ className?: string }> = ({ className }) => (
  // Hammer (Lucide) — literal "build", pairs with the chat bubble's outline weight.
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <path d="m15 12-8.373 8.373a1 1 0 1 1-3-3L12 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="m18 15 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="m21.5 11.5-1.914-1.914A2 2 0 0 1 19 8.172V7l-2.26-2.26a6 6 0 0 0-4.202-1.756L9 2.96l.92.82A6.18 6.18 0 0 1 12 8.4V10l2 2h1.172a2 2 0 0 1 1.414.586L18.5 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ALL_MODE_TABS: { mode: Mode; label: string; to: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  // Build leads (Aima, 2026-08-11) — the builder is the primary surface, chat second.
  { mode: 'build', label: 'Build', to: '/apps', Icon: BuildModeIcon },
  { mode: 'chat', label: 'Chat', to: '/chat', Icon: ChatModeIcon },
];
// Chat hidden for launch (CHAT_MODE_HIDDEN): Build alone, so no switch at all.
const MODE_TABS = CHAT_MODE_HIDDEN ? ALL_MODE_TABS.filter((t) => t.mode !== 'chat') : ALL_MODE_TABS;
const SHOW_MODE_SWITCH = MODE_TABS.length > 1;

// Team + Connectors are constant across both modes.
const TEAM_ITEM: NavItemSpec = { to: '/team', label: 'Team', Icon: Users };
// "Connectors" — opens the global ConnectorsModalProvider via
// `?connectors` on the current pathname. The /connectors route still
// exists as a redirect shim (preserves OAuth callbacks + old
// bookmarks), but the nav now keeps the user in their current context
// (composer state, task in progress) instead of unloading the page.
const CONNECTORS_ITEM: NavItemSpec = { to: '/connectors', label: 'Connectors', Icon: PuzzlePiece, modal: true, modalParam: 'connectors' };

const GiftIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <rect x="3" y="8" width="18" height="4" rx="1" />
    <path d="M12 8v13" />
    <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
    <path d="M7.5 8a2.5 2.5 0 0 1 0-5C10 3 12 8 12 8s2-5 4.5-5a2.5 2.5 0 0 1 0 5" />
  </svg>
);

const TemplatesIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
  </svg>
);

// Code mode surfaces New app + your apps library (/apps/all — the full
// library). Chat mode drops those and shows Recent chats + New chat instead.
const BUILD_NAV: NavItemSpec[] = [
  { to: '/apps/all', label: 'All Apps', Icon: Folder },
  { to: '/templates', label: 'Templates', Icon: TemplatesIcon },
  TEAM_ITEM,
  CONNECTORS_ITEM,
];
const CHAT_NAV: NavItemSpec[] = [TEAM_ITEM, CONNECTORS_ITEM];

// Staff-only admin surface (Aima ↔ Numan 2026-09-14): lives inside the app,
// not on a subdomain. The entry point is the Admin tab on /settings (Aima,
// 2026-09-16 — not a row in this nav list); ADMIN_NAV is what the sidebar
// shows once you're actually on the admin surface.
const ADMIN_PATH = '/ops-moonlight-admin';
// `exact` on Dashboard: without it, isActive(ADMIN_PATH) would also match
// /ops-moonlight-admin/machines (startsWith(path + '/')), lighting up both
// nav items at once.
const ADMIN_NAV: NavItemSpec[] = [
  // Dashboard is the admin landing page; Users moved to /users under it.
  { to: ADMIN_PATH, label: 'Dashboard', Icon: BarChart, exact: true },
  { to: `${ADMIN_PATH}/users`, label: 'Users', Icon: Users },
  { to: `${ADMIN_PATH}/machines`, label: 'Machines', Icon: ServerStack },
  { to: `${ADMIN_PATH}/credit`, label: 'Credit', Icon: PuzzlePiece },
  { to: `${ADMIN_PATH}/invites`, label: 'Invite links', Icon: LinkChain },
  { to: `${ADMIN_PATH}/referral-program`, label: 'Referrals', Icon: GiftIcon },
];

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle, isMobile = false, mobileOpen = false, onMobileClose, onMobileOpen, expandedWidth, onResize, onResizingChange }) => {
  const { user, logout } = useAuth();
  const { startTour, run } = useCoachMark();
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (path: string, exact?: boolean) =>
    location.pathname === path || (!exact && location.pathname.startsWith(path + '/'));

  // Workspace mode (Build ⇄ Chat ⇄ Admin). Derived from the path when we're
  // clearly on one surface; on shared pages (Team, Connectors, Settings) it
  // falls back to the last explicit choice — persisted — so the rail doesn't
  // flip out from under you when you pop into Team from either side. Admin
  // is path-only: it's never the persisted fallback (see WorkspaceMode).
  const pathMode: Mode | null =
    location.pathname === ADMIN_PATH || location.pathname.startsWith(ADMIN_PATH + '/')
      ? 'admin'
      : location.pathname === '/apps' || location.pathname.startsWith('/apps/') || location.pathname.startsWith('/tasks') || location.pathname === '/templates'
      ? 'build'
      : location.pathname === '/chat' || location.pathname.startsWith('/chat/') ||
        // legacy paths still resolve to chat during the redirect frame
        location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/') ||
        location.pathname === '/chats' || location.pathname.startsWith('/chats/')
      ? 'chat'
      : null;
  const [storedMode, setStoredMode] = useState<WorkspaceMode>(() => {
    if (CHAT_MODE_HIDDEN) return 'build';
    const s = (typeof window !== 'undefined' ? window.localStorage.getItem(WORKSPACE_MODE_KEY) : null) as WorkspaceMode | null;
    return s === 'chat' || s === 'build' ? s : 'build';
  });
  // A chat path only shows for the redirect frame while Chat is hidden.
  const mode: Mode = CHAT_MODE_HIDDEN && pathMode === 'chat' ? 'build' : pathMode ?? storedMode;
  useEffect(() => {
    // Only build/chat are ever persisted — admin (and the null "unknown path"
    // case) must never overwrite the remembered workspace.
    if (pathMode !== 'build' && pathMode !== 'chat') return;
    if (CHAT_MODE_HIDDEN) return;
    if (pathMode === storedMode) return;
    setStoredMode(pathMode);
    try { window.localStorage.setItem(WORKSPACE_MODE_KEY, pathMode); } catch { /* localStorage may be unavailable */ }
  }, [pathMode, storedMode]);

  // Balance scope (pricing gate design D7): the org that owns the open app on
  // /apps/:id, the personal org everywhere else the sidebar shows.
  const { balance, visible: billingVisible, setScopeProjectId, scopeProjectId } = useBilling();
  const appMatch = /^\/apps\/([a-f0-9]{24})(?:\/|$)/.exec(location.pathname);
  const routeProjectId = appMatch ? appMatch[1] : null;
  useEffect(() => { setScopeProjectId(routeProjectId); }, [routeProjectId, setScopeProjectId]);
  const plansHref = scopeProjectId ? `/plans?project=${scopeProjectId}` : '/plans';
  const showBalance = !!(billingVisible && balance && mode !== 'admin');

  const homeHref = mode === 'build' ? '/apps' : mode === 'chat' ? '/chat' : ADMIN_PATH;
  // Where "Back to app" returns to from admin mode — the last workspace you
  // were actually on, not whichever of build/chat happens to be default.
  const backHref = storedMode === 'chat' ? '/chat' : '/apps';
  // Connectors are hidden unless the server enables them for this account.
  const connectorsVisible = useConnectorsVisible();
  const navItems = (mode === 'admin' ? ADMIN_NAV : mode === 'build' ? BUILD_NAV : CHAT_NAV)
    .filter((item) => connectorsVisible || item !== CONNECTORS_ITEM);
  const goToMode = (to: string) => {
    navigate(to);
    if (isMobile && onMobileClose) onMobileClose();
  };

  // ⌘. / Ctrl+. toggles Build ⇄ Chat from anywhere (⌘1/⌘2 are reserved by the
  // browser for tab switching, so the toggle gets a single unclaimed chord).
  // In admin mode it's "back to app" rather than a toggle — there's no
  // second admin surface to switch to.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key === '.') {
        // Chat hidden: nothing to toggle to; admin still goes back.
        if (CHAT_MODE_HIDDEN && mode !== 'admin') return;
        e.preventDefault();
        navigate(mode === 'admin' ? backHref : mode === 'build' ? '/chat' : '/apps');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, navigate, backHref]);
  const isMac = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform);
  const modeShortcutHint = isMac ? '⌘.' : 'Ctrl+.';

  // Avatar popover — click trigger opens a small menu with Settings + Log out.
  // Closes on outside click, Escape, or selecting an item.
  const [menuOpen, setMenuOpen] = useState(false);

  // Chat history (Perplexity-style) — lives in the shared sidebar below the nav
  // links. Chat home is /chat; a conversation is /chat/:id. We also match the
  // legacy /chats/:id and /dashboard/:id prefixes so the row still highlights
  // during the redirect frame and for any old link that hasn't bounced yet.
  const [chats, setChats] = useState<Array<{ _id: string; title: string }>>([]);
  const activeConvId = location.pathname.startsWith('/chat/') || location.pathname.startsWith('/chats/') || location.pathname.startsWith('/dashboard/')
    ? location.pathname.split('/')[2]
    : null;
  const loadChats = useCallback(() => {
    if (!user) return;
    conversationsAPI
      .list()
      .then((r) => setChats((r.data && r.data.conversations) || []))
      .catch(() => {});
  }, [user]);
  // Refetch on navigation + whenever ChatPage signals a change (new chat created
  // or a turn finished, by which point the auto-generated title is set).
  useEffect(() => { loadChats(); }, [loadChats, location.pathname]);
  useEffect(() => {
    const onRefresh = () => loadChats();
    window.addEventListener('conversations:refresh', onRefresh);
    return () => window.removeEventListener('conversations:refresh', onRefresh);
  }, [loadChats]);

  // Per-chat actions menu (rename / delete). Rename is an INLINE edit (no native
  // prompt); delete uses an in-app confirm (no native confirm).
  const [menuChatId, setMenuChatId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const startRename = (id: string, current: string) => {
    setMenuChatId(null);
    setRenameValue(current || '');
    setRenamingId(id);
  };
  const commitRename = async (id: string) => {
    const next = renameValue.trim();
    setRenamingId(null);
    const cur = chats.find((c) => c._id === id);
    if (!next || (cur && cur.title === next)) return; // unchanged / empty → no-op
    try { await conversationsAPI.rename(id, next); loadChats(); } catch { /* ignore */ }
  };
  const confirmDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    if (!id) return;
    try {
      await conversationsAPI.remove(id);
      if (activeConvId === id) navigate('/chat');
      loadChats();
    } catch { /* ignore */ }
  };

  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleSettings = () => {
    setMenuOpen(false);
    navigate('/settings');
  };
  const handleMemory = () => {
    setMenuOpen(false);
    navigate('/memory');
  };
  // Refer & earn (/referrals): the user's referral link and progress.
  const handleReferrals = () => {
    setMenuOpen(false);
    navigate('/referrals');
  };
  const handleShowMeAround = (trigger: HTMLElement | null) => {
    setMenuOpen(false);
    // Page-aware: the tour for wherever the person is (falls back to home).
    startTour(null, { trigger, source: 'manual' });
  };

  // Bulb glow: only while no run is already open, and only for the tour that
  // owns the current page — 'new' (never seen) or 'update' (seen an older
  // version with new cards since). See lib/coachRun.ts's tourGlowState.
  const bulbTourId = tourForRoute(location.pathname);
  const bulbGlow = !run && bulbTourId ? tourGlowState(COACH_TOURS[bulbTourId], getTourRecord(user, bulbTourId)) : null;
  const handleLogout = () => {
    setMenuOpen(false);
    logout();
  };

  const sidebarWidth = isMobile ? SIDEBAR_W_EXPANDED : (collapsed ? SIDEBAR_W_COLLAPSED : (expandedWidth ?? SIDEBAR_W_EXPANDED));

  // Drag-resize (desktop, expanded only) — same pointer pattern as
  // ResizableAside. The aside is anchored at left:0, so width = cursor X.
  // Width transition is suppressed mid-drag so the panel tracks the cursor
  // instead of rubber-banding behind it.
  const [resizing, setResizing] = useState(false);
  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    setResizing(true);
    onResizingChange?.(true);
    const move = (ev: PointerEvent) => {
      onResize?.(Math.min(Math.max(ev.clientX, SIDEBAR_MIN_W), SIDEBAR_MAX_W));
    };
    const up = () => {
      setResizing(false);
      onResizingChange?.(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const drawerOpen = isMobile && mobileOpen;

  // Phone tour: the drawer covers the page, so it is open exactly while the
  // card points at something inside it (All Apps, credit, the bulb…) and shut
  // while the card points at the page (the tabs, the composer). Otherwise the
  // page cards ring things hidden behind the drawer.
  const asideRef = useRef<HTMLElement | null>(null);
  const tourStep = run && run.kind === 'tour' ? currentStep(run.steps, run.index, readCoachState()) : null;
  const tourAnchor = tourStep ? tourStep.anchor : null;
  useEffect(() => {
    if (!isMobile || !tourAnchor) return;
    const inDrawer = !!asideRef.current?.querySelector(`[data-coach="${tourAnchor}"]`);
    if (inDrawer && !mobileOpen) onMobileOpen?.();
    else if (!inDrawer && mobileOpen) onMobileClose?.();
  }, [isMobile, tourAnchor, mobileOpen, onMobileOpen, onMobileClose]);

  useEffect(() => {
    if (!drawerOpen || !onMobileClose) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onMobileClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen, onMobileClose]);

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-30"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}
    <aside
      ref={asideRef}
      className="fixed top-0 left-0 h-dvh z-40 flex flex-col"
      style={{
        width: sidebarWidth,
        background: '#000000',
        // Desktop: <main> is a raised sheet whose own left border draws this
        // seam, and two abutting 1px lines read as one 2px line. Mobile keeps
        // it — the drawer floats over the content, so it butts against nothing.
        borderRight: isMobile ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
        transition: resizing
          ? 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
          : 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        fontFamily: 'DM Sans, sans-serif',
        transform: isMobile ? (mobileOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
      }}
      aria-label="Primary navigation"
      // Off-screen drawer must not stay in the tab order or the a11y tree.
      inert={isMobile && !mobileOpen}
      aria-hidden={isMobile && !mobileOpen ? true : undefined}
    >
      {/* Right-edge drag handle — desktop + expanded only. Hairline brightens
          on hover (mirrors ResizableAside); double-click resets the default. */}
      {!isMobile && !collapsed && onResize && (
        <div
          onPointerDown={startResize}
          onDoubleClick={() => onResize(SIDEBAR_W_EXPANDED)}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          title="Drag to resize · double-click to reset"
          className="group absolute right-0 top-0 z-50 h-full w-2 translate-x-1/2 cursor-col-resize"
        >
          <div className="mx-auto h-full w-px bg-transparent transition-colors group-hover:bg-[#3264F8]/60" />
        </div>
      )}
      {/* Transparent overlay keeps pointer events off iframes/text mid-drag. */}
      {resizing && <div className="fixed inset-0 z-[70] cursor-col-resize" />}
      {/* Header — logo + toggle on the same row when expanded, stacked when collapsed.
          On mobile the sidebar is a full-width drawer so always show the expanded header. */}
      {!isMobile && collapsed ? (
        /* Collapsed: the logo IS the expand control — hovering (or keyboard-
           focusing) it swaps the mark for the panel icon, Claude-style. One
           affordance instead of two stacked buttons. Home nav stays reachable
           from the expanded header. */
        <div className="flex flex-col items-center pt-3 pb-2">
          <button
            type="button"
            onClick={onToggle}
            aria-label="Expand sidebar"
            aria-expanded={false}
            className="group relative flex items-center justify-center rounded-lg hover:bg-white/[0.04] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3264F8]"
            style={{ width: 36, height: 36 }}
          >
            <RailTooltip label="Expand sidebar" />
            <span className="flex items-center justify-center group-hover:hidden group-focus-visible:hidden">
              <svg width="22" height="22" viewBox="0 0 34.95 40.29" aria-hidden="true">
                <defs>
                  <linearGradient id="sb-logo-grad" x1="0" y1="20.15" x2="34.95" y2="20.15" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#2f69fa" />
                    <stop offset=".36" stopColor="#6d49e7" />
                    <stop offset=".52" stopColor="#a04ebd" />
                    <stop offset=".66" stopColor="#db538c" />
                    <stop offset=".71" stopColor="#f1567b" />
                    <stop offset="1" stopColor="#fe813b" />
                  </linearGradient>
                </defs>
                <path
                  fill="url(#sb-logo-grad)"
                  d="M32.35,15.75L6.02.54C3.34-1,0,.93,0,4.02v31.97c0,3.31,3.58,5.38,6.45,3.72l25.9-14.95c3.47-2,3.47-7,0-9Z"
                />
              </svg>
            </span>
            <span className="hidden items-center justify-center text-gray-300 group-hover:flex group-focus-visible:flex">
              <PanelToggleIcon />
            </span>
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between" style={{ height: 64, padding: '0 16px 0 20px' }}>
          <Link to={homeHref} className="flex items-center gap-2 min-w-0" aria-label="Founding.dev — back to home">
            <svg width="22" height="22" viewBox="0 0 34.95 40.29" aria-hidden="true" style={{ flexShrink: 0 }}>
              <defs>
                <linearGradient id="sb-logo-grad" x1="0" y1="20.15" x2="34.95" y2="20.15" gradientUnits="userSpaceOnUse">
                  <stop offset="0" stopColor="#2f69fa" />
                  <stop offset=".36" stopColor="#6d49e7" />
                  <stop offset=".52" stopColor="#a04ebd" />
                  <stop offset=".66" stopColor="#db538c" />
                  <stop offset=".71" stopColor="#f1567b" />
                  <stop offset="1" stopColor="#fe813b" />
                </linearGradient>
              </defs>
              <path
                fill="url(#sb-logo-grad)"
                d="M32.35,15.75L6.02.54C3.34-1,0,.93,0,4.02v31.97c0,3.31,3.58,5.38,6.45,3.72l25.9-14.95c3.47-2,3.47-7,0-9Z"
              />
            </svg>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15, delay: 0.05 }}
              className="text-white font-bold text-base whitespace-nowrap"
              style={{ letterSpacing: '-0.02em' }}
            >
              Founding.dev
            </motion.span>
          </Link>
          <button
            type="button"
            onClick={isMobile ? onMobileClose : onToggle}
            aria-label={isMobile ? 'Close navigation' : 'Collapse sidebar'}
            aria-expanded={isMobile ? undefined : true}
            title={isMobile ? 'Close navigation' : 'Collapse sidebar'}
            className="flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors duration-150 flex-shrink-0"
            style={{ width: isMobile ? 44 : 32, height: isMobile ? 44 : 32 }}
          >
            {isMobile ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            ) : (
              <PanelToggleIcon />
            )}
          </button>
        </div>
      )}

      {/* Nav — extra top margin so the first nav item doesn't crowd the logo. */}
      {user && (
        <nav className="px-2 pt-6 pb-2" aria-label="Primary">
          {/* Mode switcher — Build ⇄ Chat. A classic segmented control: both
              labels always visible, active segment gets a raised neutral thumb
              that slides between the two (brand blue stays reserved for
              nav-active + focus states so the sidebar keeps one accent system).
              Admin mode swaps this whole control for a single "Back to app"
              row instead — there's no second admin surface to switch to. */}
          {mode === 'admin' ? (
            (isMobile || !collapsed) ? (
              <button
                type="button"
                onClick={() => goToMode(backHref)}
                className="mb-4 flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-300 transition-colors duration-150 hover:bg-white/[0.04] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3264F8]"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Back to app
              </button>
            ) : (
              <button
                type="button"
                onClick={() => goToMode(backHref)}
                aria-label="Back to app"
                className="group relative mx-auto mb-3 flex h-9 w-9 items-center justify-center rounded-lg text-gray-300 transition-colors duration-150 hover:bg-white/[0.04] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3264F8]"
              >
                <RailTooltip label="Back to app" />
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
              </button>
            )
          ) : !SHOW_MODE_SWITCH ? null : (isMobile || !collapsed) ? (
            <div
              data-coach="home.sidebar-mode"
              role="tablist"
              aria-label="Workspace mode"
              className="mb-4 flex select-none rounded-[10px] bg-white/[0.04] p-1"
            >
              {MODE_TABS.map((t) => {
                const active = mode === t.mode;
                return (
                  <button
                    key={t.mode}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-label={`${t.label} mode`}
                    title={`${t.label} (${modeShortcutHint})`}
                    onClick={() => goToMode(t.to)}
                    className={`relative flex flex-1 items-center justify-center gap-2 rounded-md py-2 text-[13px] font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3264F8] ${
                      active ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                    }`}
                  >
                    {active && (
                      <motion.span
                        layoutId="fd-mode-thumb"
                        transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                        className="absolute inset-0 rounded-md bg-[#1d1d21] ring-1 ring-white/[0.08]"
                        style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.6), 0 1px 1px rgba(0, 0, 0, 0.4)' }}
                        aria-hidden
                      />
                    )}
                    <t.Icon className="relative flex-shrink-0" />
                    <span className="relative">{t.label}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            /* Collapsed (64px icon rail): same segmented control, stacked
               vertically as icon-only tabs. */
            <div data-coach="home.sidebar-mode" className="mb-3 flex flex-col items-center gap-1 rounded-[10px] bg-white/[0.04] p-1" role="tablist" aria-label="Workspace mode">
              {MODE_TABS.map((t) => {
                const active = mode === t.mode;
                return (
                  <button
                    key={t.mode}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-label={`${t.label} mode`}
                    onClick={() => goToMode(t.to)}
                    className={`group relative flex items-center justify-center rounded-md transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3264F8] ${
                      active ? 'bg-[#1d1d21] text-white ring-1 ring-white/[0.08]' : 'text-gray-500 hover:text-gray-300'
                    }`}
                    style={active ? { width: 34, height: 32, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.6)' } : { width: 34, height: 32 }}
                  >
                    <RailTooltip label={`${t.label} · ${modeShortcutHint}`} />
                    <t.Icon />
                  </button>
                );
              })}
            </div>
          )}
          {/* Legible surface swap: labels the nav list that follows once
              you're actually inside the admin surface. */}
          {mode === 'admin' && (isMobile || !collapsed) && (
            <div className="mb-1 px-3 text-[11px] uppercase tracking-wide text-gray-500">Admin</div>
          )}
          {/* New — Code mode's primary action, mirroring Chat mode's New
              chat row: quiet nav-row above the nav list, straight to the
              create composer. Deliberately kind-agnostic ("New", not "New
              app"): the composer offers landing pages today and more kinds
              later, and a landing page isn't an app. */}
          {mode === 'build' && (
          <button
            type="button"
            onClick={() => { navigate('/apps'); if (isMobile && onMobileClose) onMobileClose(); }}
            aria-label="New"
            className={`group relative mb-1 flex items-center rounded-lg text-gray-200 transition-colors duration-150 hover:bg-white/[0.04] hover:text-white ${
              (!isMobile && collapsed) ? 'mx-auto h-9 w-9 justify-center' : 'w-full gap-3 px-3 py-2.5 min-h-11 md:min-h-0'
            }`}
          >
            {!isMobile && collapsed && <RailTooltip label="New" />}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {(isMobile || !collapsed) && (
              <span className="text-sm font-medium whitespace-nowrap">New</span>
            )}
          </button>
          )}
          {/* New chat — chat mode only. Always returns to the empty-composer
              chat home. Styled as a quiet nav-row (no border/box) so it reads
              as the first action in the list, not a competing widget. */}
          {mode === 'chat' && (
          <button
            type="button"
            onClick={() => { navigate('/chat'); if (isMobile && onMobileClose) onMobileClose(); }}
            aria-label="New chat"
            className={`group relative mb-1 flex items-center rounded-lg text-gray-200 transition-colors duration-150 hover:bg-white/[0.04] hover:text-white ${
              (!isMobile && collapsed) ? 'mx-auto h-9 w-9 justify-center' : 'w-full gap-3 px-3 py-2.5 min-h-11 md:min-h-0'
            }`}
          >
            {!isMobile && collapsed && <RailTooltip label="New chat" />}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {(isMobile || !collapsed) && (
              <span className="text-sm font-medium whitespace-nowrap">New chat</span>
            )}
          </button>
          )}
          <ul className="space-y-1">
            {navItems.map(({ to, label, Icon, modal, modalParam, exact }) => {
              // Modal nav items are "active" when their URL param is set,
              // not when the pathname matches. Route nav items use the
              // existing pathname-based check.
              const params = new URLSearchParams(location.search);
              const active = modal && modalParam
                ? params.has(modalParam)
                : isActive(to, exact);
              // Modal items keep the user's current pathname and append
              // the modal param — back button closes the modal naturally,
              // composer/task state survives.
              const linkTo = modal && modalParam
                ? { pathname: location.pathname, search: `?${modalParam}` }
                : to;
              return (
                <li key={to} data-coach={to === '/apps/all' ? 'home.sidebar-apps' : to === '/templates' ? 'home.sidebar-templates' : undefined}>
                  <Link
                    to={linkTo}
                    aria-current={active ? 'page' : undefined}
                    onClick={isMobile ? onMobileClose : undefined}
                    className={`group relative flex items-center rounded-lg transition-colors duration-150 ${
                      (!isMobile && collapsed) ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5 min-h-11 md:min-h-0'
                    } ${
                      active
                        ? 'bg-[#2f69fa]/12 text-[#5a8afc]'
                        : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                    }`}
                  >
                    {!isMobile && collapsed && <RailTooltip label={label} />}
                    <Icon size={20} className="flex-shrink-0" />
                    {(isMobile || !collapsed) && (
                      <span className="text-sm font-medium whitespace-nowrap">{label}</span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>

        </nav>
      )}

      {/* Chat history (Perplexity-style) — chat mode only, below the nav links,
          fills the remaining space and scrolls. Hidden when collapsed to icons. */}
      {user && mode === 'chat' && (isMobile || !collapsed) && (
        <div className="flex-1 min-h-0 flex flex-col px-2 pt-8 pb-2 overflow-hidden">
          {menuChatId && <div className="fixed inset-0 z-40" onClick={() => setMenuChatId(null)} aria-hidden />}
          {/* In-app delete confirm (replaces the native confirm dialog). */}
          {confirmDeleteId && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true">
              <div className="absolute inset-0 bg-black/60" onClick={() => setConfirmDeleteId(null)} aria-hidden />
              <div className="relative w-full max-w-sm rounded-xl border border-white/10 bg-[#1a1a1a] p-5 shadow-2xl">
                <div className="text-sm font-medium text-white">Delete this chat?</div>
                <div className="mt-1 text-xs text-gray-400">It’ll be removed from your chats.</div>
                <div className="mt-4 flex justify-end gap-2">
                  <button type="button" onClick={() => setConfirmDeleteId(null)} className="rounded-md px-3 py-1.5 text-sm text-gray-300 transition hover:bg-white/[0.06]">Cancel</button>
                  <button type="button" onClick={confirmDelete} className="rounded-md bg-red-500/90 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-red-500">Delete</button>
                </div>
              </div>
            </div>
          )}
          <div className="px-2 pb-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-gray-600">Recent chats</div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {chats.length === 0 ? (
              <div className="px-1 py-1 text-xs text-gray-600">No chats yet</div>
            ) : (
              <ul className="space-y-0.5">
                {chats.map((c) => (
                  <li key={c._id} className="group relative">
                    {renamingId === c._id ? (
                      // Inline rename — no native prompt. Enter saves, Esc cancels, blur saves.
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename(c._id);
                          else if (e.key === 'Escape') setRenamingId(null);
                        }}
                        onBlur={() => commitRename(c._id)}
                        aria-label="Rename chat"
                        className="block w-full rounded-md border border-[#3264F8]/60 bg-[#0a0a0a] py-1.5 px-2 text-sm text-white outline-none"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => { navigate(`/chat/${c._id}`); if (isMobile && onMobileClose) onMobileClose(); }}
                        title={c.title || 'New chat'}
                        className={`block w-full truncate rounded-md py-1.5 pl-2 pr-7 text-left text-sm transition ${
                          c._id === activeConvId
                            ? 'bg-white/[0.06] text-white'
                            : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                        }`}
                      >
                        {c.title || 'New chat'}
                      </button>
                    )}
                    {/* Hover/active actions trigger (hidden while renaming) */}
                    {renamingId !== c._id && (
                      <button
                        type="button"
                        aria-label="Chat options"
                        onClick={(e) => { e.stopPropagation(); setMenuChatId((m) => (m === c._id ? null : c._id)); }}
                        className={`touch-reveal absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 max-md:p-2 text-gray-400 hover:bg-white/10 hover:text-white ${
                          menuChatId === c._id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        }`}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>
                      </button>
                    )}
                    {menuChatId === c._id && (
                      <div className="absolute right-1 top-8 z-50 w-32 overflow-hidden rounded-lg border border-white/10 bg-[#1a1a1a] py-1 shadow-lg">
                        <button type="button" onClick={() => startRename(c._id, c.title)} className="block w-full px-3 py-1.5 text-left text-sm text-gray-200 hover:bg-white/[0.06]">Rename</button>
                        <button type="button" onClick={() => { setMenuChatId(null); setConfirmDeleteId(c._id); }} className="block w-full px-3 py-1.5 text-left text-sm text-red-400 hover:bg-white/[0.06]">Delete</button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Spacer keeps the profile pinned to the bottom whenever the Recent
          chats list (the natural flex-1 filler) isn't rendered — i.e. Build
          mode, or any collapsed state. Without this the avatar bar floats up
          right under the nav. */}
      {user && !(mode === 'chat' && (isMobile || !collapsed)) && <div className="flex-1" />}

      {/* Profile (bottom) — avatar button (opens popover). */}
      {user && (
        <div
          ref={menuRef}
          className="mx-2 mb-3 mt-1 pt-3 relative"
          style={{
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            ...(isMobile ? { marginBottom: 'max(0.75rem, env(safe-area-inset-bottom))' } : null),
          }}
        >
          {/* Balance (pricing gate design D6): directly above "Show me around",
              with its own hairline. Hidden for contract orgs and while the gate
              is not enforcing (BillingContext.visible). The same meter as the
              task page (2026-10-20): Plan credits, Extra credit when topped up,
              a usage card on hover. Still the link to Plans, as before ("See
              plans" / "Get more" under it); collapsed, the ring opens the sidebar. */}
          {showBalance && balance && (
            <>
              <div data-coach="home.credit">
                <TaskCreditMeter balance={balance} collapsed={!isMobile && collapsed} onExpand={onToggle} plansTo={plansHref} onNavigate={isMobile ? onMobileClose : undefined} />
              </div>
              <div aria-hidden="true" className="my-2.5" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }} />
            </>
          )}
          <button
            type="button"
            data-coach="home.bulb"
            onClick={(e) => handleShowMeAround(e.currentTarget)}
            aria-label="Show me around"
            className={`group relative flex items-center rounded-lg transition-colors duration-150 text-gray-400 hover:text-gray-200 hover:bg-white/[0.04] focus:outline-none focus-visible:bg-white/[0.04] mb-2.5 ${
              (!isMobile && collapsed) ? 'justify-center p-1.5 mx-auto' : 'w-full gap-2.5 px-2 py-1.5'
            }`}
          >
            {!isMobile && collapsed && <RailTooltip label="Show me around" />}
            <span className={`relative flex-shrink-0 inline-flex ${bulbGlow ? 'fd-bulb-glow' : ''}`} data-glow={bulbGlow ?? undefined}>
              <BulbIcon size={16} />
            </span>
            {(isMobile || !collapsed) && <span className="text-sm font-medium whitespace-nowrap">Show me around</span>}
          </button>
          {/* Report a bug: opens Intercom to type the issue (Aima, 2026-10-15).
              Same row shape as "Show me around"; icon-only on the rail. */}
          <button
            type="button"
            onClick={() => openBugReport()}
            aria-label="Report a bug"
            className={`group relative flex items-center rounded-lg transition-colors duration-150 text-gray-400 hover:text-gray-200 hover:bg-white/[0.04] focus:outline-none focus-visible:bg-white/[0.04] mb-2.5 ${
              (!isMobile && collapsed) ? 'justify-center p-1.5 mx-auto' : 'w-full gap-2.5 px-2 py-1.5'
            }`}
          >
            {!isMobile && collapsed && <RailTooltip label="Report a bug" />}
            <span className="relative flex-shrink-0 inline-flex">
              <HelpIcon size={16} />
            </span>
            {(isMobile || !collapsed) && <span className="text-sm font-medium whitespace-nowrap">Report a bug</span>}
          </button>
          {/* Hairline between the tour bulb and the account row (Aima, 2026-09-24):
              the two are different jobs and read as one block without it. Same
              rule as the footer's own top border. */}
          <div aria-hidden="true" className="mb-2.5" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }} />
          <div className={`flex ${(!isMobile && collapsed) ? 'flex-col items-center gap-1' : 'flex-row items-center gap-1'}`}>
            {/* Notification bell — above avatar when collapsed, beside it when expanded */}
            {(!isMobile && collapsed) && (
              <div className="flex-shrink-0">
                <NotificationBell collapsed={true} isMobile={isMobile} />
              </div>
            )}
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              data-coach="home.account"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={`Account menu for ${user.name}`}
              title={(!isMobile && collapsed) ? user.name : undefined}
              className={`flex items-center rounded-lg transition-colors duration-150 focus:outline-none focus:bg-white/[0.04] min-w-0 flex-1 ${
                (!isMobile && collapsed) ? 'p-1.5 justify-center flex-none' : 'gap-2.5 px-2 py-1.5'
              } ${menuOpen ? 'bg-white/[0.04]' : 'hover:bg-white/[0.04]'}`}
            >
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="rounded-full ring-1 ring-white/10 flex-shrink-0"
                  style={{ width: 28, height: 28 }}
                />
              ) : (
                <div
                  className="rounded-full bg-white/10 flex items-center justify-center flex-shrink-0"
                  style={{ width: 28, height: 28 }}
                >
                  <UserIcon className="w-4 h-4 text-gray-300" />
                </div>
              )}
              {(isMobile || !collapsed) && (
                <>
                  <div className="min-w-0 text-left">
                    <div className="text-sm font-medium text-white truncate" style={{ letterSpacing: '-0.01em' }}>
                      {user.name}
                    </div>
                    {user.email && (
                      <div className="text-[11px] text-gray-500 truncate">{user.email}</div>
                    )}
                  </div>
                  <ChevronDownIcon
                    className={`w-3.5 h-3.5 text-gray-500 flex-shrink-0 ml-1 transition-transform duration-150 ${menuOpen ? 'rotate-180' : ''}`}
                  />
                </>
              )}
            </button>

            {/* Notification bell — beside avatar when expanded */}
            {(isMobile || !collapsed) && (
              <div className="flex-shrink-0 mr-1">
                <NotificationBell collapsed={true} isMobile={isMobile} />
              </div>
            )}

          </div>

          <AnimatePresence>
            {menuOpen && (
              <motion.div
                role="menu"
                aria-label="Account"
                initial={{ opacity: 0, y: 6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 4, scale: 0.98 }}
                transition={{ duration: 0.12, ease: 'easeOut' }}
                className="absolute z-50 rounded-xl border py-1.5"
                style={{
                  background: '#111113',
                  borderColor: 'rgba(255,255,255,0.08)',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.25)',
                  width: 200,
                  ...(!isMobile && collapsed
                    ? { left: 'calc(100% + 8px)', bottom: 4 }
                    : { left: 0, right: 0, bottom: 56, width: 'auto' }),
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleSettings}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-white/[0.05] hover:text-white transition-colors duration-100 text-left"
                >
                  <SettingsFuture size={16} className="flex-shrink-0 text-gray-400" />
                  <span>Settings</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleMemory}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-white/[0.05] hover:text-white transition-colors duration-100 text-left"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0 text-gray-400"><path d="M12 2a4 4 0 0 0-4 4v1a3 3 0 0 0-3 3v1a3 3 0 0 0 0 6 3 3 0 0 0 3 3 4 4 0 0 0 8 0 3 3 0 0 0 3-3 3 3 0 0 0 0-6V10a3 3 0 0 0-3-3V6a4 4 0 0 0-4-4z" /></svg>
                  <span>Memory</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleReferrals}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-white/[0.05] hover:text-white transition-colors duration-100 text-left"
                >
                  <GiftIcon size={16} className="flex-shrink-0 text-gray-400" />
                  <span>Refer &amp; earn</span>
                </button>
                <div className="my-1 mx-2" style={{ height: 1, background: 'rgba(255,255,255,0.05)' }} />
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-white/[0.05] hover:text-white transition-colors duration-100 text-left"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4 flex-shrink-0 text-gray-400" />
                  <span>Log out</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </aside>
    </>
  );
};

export default Sidebar;
